import { heapEvent } from '@equipmentshare/heaper'
import { AddProductModal, EditProductModal, MergeProductsModal } from './components/ProductModals'
import { GoodProduct, Part } from '../../../graphql'

// Maps a GoodProduct to the Part-shaped object that the existing edit/merge modals expect.
// Assumes the numeric portion of GoodProduct.id matches Part.id, and that
// GoodProduct.manufacturer maps to Part.provider.
function toPartShape( product: GoodProduct ): Part {
	return {
		...product,
		id: Number( product.id ),
		partNumber: product.manufacturerNumber ?? undefined,
		provider: product.manufacturer
			? { id: Number( product.manufacturer.id ), name: product.manufacturer.name }
			: ( undefined as any ),
		partClass: { id: 0, description: product.name },
	} as unknown as Part
}

export function getProductAddModal( onSuccess: () => void ) {
	return {
		component: AddProductModal,
		label: 'Add Product',
		onSuccess,
		onXClick: () => heapEvent( 'Inventory - Add Part Modal - Click - X button', {} ),
	}
}

export function getProductEditModal(
	product: GoodProduct,
	onMerge: ( product: GoodProduct ) => void,
	onSuccess?: () => void,
) {
	const standardPrice = product.standardPrice ? new Intl.NumberFormat(
		'en-US',
		{
			style: 'decimal',
			currency: product.standardPrice.currencyCode,
			maximumFractionDigits: 4,
			minimumFractionDigits: 2,
			useGrouping: false,
		}
	).format( product.standardPrice.amount ) : ''

	const msrp = product.msrp ? new Intl.NumberFormat(
		'en-US',
		{
			style: 'decimal',
			currency: 'USD',
			maximumFractionDigits: 4,
			minimumFractionDigits: 2,
			useGrouping: false,
		}
	).format( product.msrp ) : ''

	const productClass = product.productClass
	const partShape = toPartShape( product )

	return {
		component: EditProductModal,
		componentProps: {
			onMerge: () => onMerge( product ),
			part: partShape,
		},
		formData: {
			partId: Number( product.id ),
			provider: product.manufacturer
				? { id: Number( product.manufacturer.id ), name: product.manufacturer.name }
				: undefined,
			partNumber: product.manufacturerNumber,
			description: product.name,
			upc: product.upc,
			standardPrice,
			msrp,
			productClass,
			isBulk: !!productClass,
		},
		label: 'Edit Product',
		onSuccess,
		onXClick: () => heapEvent( 'Inventory - Edit Part Modal - Click - X button', {} ),
	}
}

export function getProductMergeModal( product: GoodProduct, onSuccess: () => void ) {
	return {
		component: MergeProductsModal,
		formData: { part: toPartShape( product ) },
		label: 'Merge Products',
		onSuccess,
		onXClick: () => heapEvent( 'Inventory - Merge Parts Modal - Click - X button', {} ),
	}
}
